import * as React from "react";
import { Box, FormControlLabel, Radio } from "@mui/material";
import { getAllOwners, getOwnerWiseReport, getOwnerExpenseReport } from "state/apis";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./index.css";
import { TextField, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import moment from "moment";
import DefaultProperty from "components/DefaultProperty";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Autocomplete from "@mui/material/Autocomplete";
import { formatAmount } from "utils/constants";

const OwnerWiseExpenseReport = () => {
    const propertyName = useSelector((state) => state.global.propertyName);
    const property = useSelector((state) => state.global.defaultProperty);
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState({});
    const [openDefaultProperty, setOpenDefaultProperty] = React.useState(false);
    const [monthChecked, setMonthChecked] = React.useState(true);
    const [dateChecked, setDateChecked] = React.useState(false);
    const [owners, setOwners] = React.useState([]);
    const start = moment().startOf("month").format("YYYY-MM-DD");
    const end = moment().endOf("month").format("YYYY-MM-DD");
    const currentMonth = moment().format("YYYY-MM");

    const [filter, setFilter] = React.useState({
        startDate: start,
        endDate: end,
        monthValue: currentMonth,
        ownerId: null,
        ownerName: "",
        totalNet: 0,
    });

    React.useEffect(() => {
        setIsLoading(true);
        fetchOwners();
        if (!property) {
            setOpenDefaultProperty(true);
        }
        setIsLoading(false);
    }, []);

    React.useEffect(() => {
        const { ownerId, startDate, endDate } = filter;

        setIsLoading(true);
        if (ownerId !== null) {
            fetchData();
        } else {
            setData([]);
            setIsLoading(false);
        }
    }, [filter.ownerId, filter.startDate, filter.endDate, monthChecked]);

    const fetchData = async () => {
        try {
            const res = await getOwnerExpenseReport({
                ownerId: filter.ownerId,
                startDate: filter.startDate,
                endDate: filter.endDate,
            });
            setData(res.data);
            setIsLoading(false);
            const totalNet = res?.totalNet || 0;
            setFilter((prevData) => ({
                ...prevData,
                totalNet: totalNet,
            }));
        } catch (error) {
            console.error("Error fetching data:", error);
            setIsLoading(false);
        }
    };

    const fetchOwners = async () => {
        try {
            const res = await getAllOwners(100000, 0);
            setOwners(res?.owners);
        } catch (error) {
            console.error("Error fetching owners:", error);
            toast.error("Failed to fetch owners. Please try again.");
        }
    };

    const handleOwnerChange = (newValue) => {
        setFilter((prevData) => ({
            ...prevData,
            ownerId: newValue ? newValue.id : null,
            ownerName: newValue ? newValue.name : "",
        }));
    };

    const handleMonthChange = (event) => {
        const isChecked = event.target.checked;
        setMonthChecked(isChecked);
        if (isChecked) {
            setDateChecked(false);
        }
    };

    const handleDateChange = (event) => {
        const isChecked = event.target.checked;
        setDateChecked(isChecked);
        if (isChecked) {
            setMonthChecked(false);
        }
    };

    const handleFrom = (date) => {
        const data = date;
        const formattedDate = data.format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            startDate: formattedDate,
        }));
    };
    const handleTo = (date) => {
        const data = date;
        const formattedDate = data.format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            endDate: formattedDate,
        }));
    };
    const handleMonth = (date) => {
        if (!date) return;
        const selectedDate = dayjs(date);

        const startDate = selectedDate.startOf("month").format("YYYY-MM-DD");
        const endDate = selectedDate.endOf("month").format("YYYY-MM-DD");

        setFilter((prevData) => ({
            ...prevData,
            startDate: startDate,
            endDate: endDate,
            monthValue: date ? date.format("YYYY-MM") : null,
        }));
    };

    const handleCloseDefaultProperty = () => {
        setOpenDefaultProperty(false);
    };

    const reportRef = React.useRef();

    const handlePrint = () => {
        const input = reportRef.current;

        html2canvas(input, { scale: 2 }).then((canvas) => {
            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const margin = 10; // Add margin (padding)

            // Get the image height and scale it proportionally
            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * (pdfWidth - 2 * margin)) / imgProps.width;

            let heightLeft = imgHeight;
            let position = margin;

            pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
            heightLeft -= pdfHeight - 2 * margin;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight + margin;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", margin, position, pdfWidth - 2 * margin, imgHeight);
                heightLeft -= pdfHeight - 2 * margin;
            }

            pdf.save(`total-expense-report.pdf`);
        });
    };

    let totalAmount = 0;
    return (
        <Box m="1.5rem 2.5rem" mb={5}>
            <Box display="flex" justifyContent="flex-end" pt={0} paddingLeft={0}>
                <Grid container spacing={1} p={"5px"} alignItems="center">
                    <Grid item xs={3}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={owners}
                            getOptionLabel={(option) => option.name}
                            value={
                                filter.ownerId
                                    ? owners.find((owner) => owner.id === filter.ownerId)
                                    : null
                            }
                            onChange={(event, newValue) => handleOwnerChange(newValue)}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.name}
                                </li>
                            )}
                            sx={{
                                width: "100%",
                                "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                        borderColor: "primary.main",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    "&.Mui-focused": {
                                        color: "primary.main",
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth label="Select Owner" />
                            )}
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            value="month"
                            control={
                                <Radio
                                    checked={monthChecked}
                                    onChange={handleMonthChange}
                                    color="primary"
                                />
                            }
                            label="Month"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            value="date"
                            control={
                                <Radio
                                    checked={dateChecked}
                                    onChange={handleDateChange}
                                    color="primary"
                                />
                            }
                            label="Date Wise"
                        />
                    </Grid>
                    {dateChecked && (
                        <>
                            <Grid item xs={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="From"
                                        name="contractFrom"
                                        onChange={handleFrom}
                                        value={filter.startDate ? dayjs(filter.startDate) : null}
                                        format="DD/MM/YYYY"
                                        sx={{
                                            width: "100%",
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                "&.Mui-focused": {
                                                    color: "primary.main",
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="To"
                                        name="contractTo"
                                        onChange={handleTo}
                                        value={filter.endDate ? dayjs(filter.endDate) : null}
                                        format="DD/MM/YYYY"
                                        sx={{
                                            width: "100%",
                                            "& .MuiOutlinedInput-root": {
                                                "&.Mui-focused fieldset": {
                                                    borderColor: "primary.main",
                                                },
                                            },
                                            "& .MuiInputLabel-root": {
                                                "&.Mui-focused": {
                                                    color: "primary.main",
                                                },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </>
                    )}
                    {monthChecked && (
                        <Grid item xs={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label={"Month"}
                                    views={["month", "year"]}
                                    name="month"
                                    onChange={handleMonth}
                                    value={filter.monthValue ? dayjs(filter.monthValue) : null}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            "&.Mui-focused fieldset": {
                                                borderColor: "primary.main",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            "&.Mui-focused": {
                                                color: "primary.main",
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    )}
                    <Grid item xs></Grid>
                    <Grid item xs="auto">
                        <button className="download-btn" onClick={handlePrint}>
                            Download PDF
                        </button>
                    </Grid>
                </Grid>
            </Box>
            {!property && (
                <DefaultProperty
                    open={openDefaultProperty}
                    onClose={() => handleCloseDefaultProperty}
                />
            )}
            {filter.ownerId && (
                <div className="report-container">
                    <div ref={reportRef}>
                        <div ref={reportRef}>
                            <table border="1" cellPadding="5" cellSpacing="0">
                                <thead>
                                    <tr>
                                        <th colSpan={4} style={{ textAlign: "center" }}>
                                            {monthChecked
                                                ? `${moment(filter.monthValue)
                                                      .format("MMMM YYYY")
                                                      .toUpperCase()}`
                                                : `${filter.startDate} TO ${filter.endDate}`}
                                        </th>
                                    </tr>
                                    <tr>
                                        <th style={{ textAlign: "center" }}>SR NO</th>
                                        <th style={{ textAlign: "center" }}>BUILDING</th>
                                        <th style={{ textAlign: "center" }}>AMOUNT</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {data && data.length ? (
                                        <>
                                            {data.map((val, index) => {
                                                totalAmount += parseFloat(val.amount);
                                                return (
                                                    <tr key={index}>
                                                        <td>{index + 1}</td>
                                                        <td>{val.propertyName}</td>
                                                        <td style={{ textAlign: "right" }}>
                                                            {formatAmount(Number(val.amount))}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: "center" }}>
                                                No data found
                                            </td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td></td>
                                        <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                            Total
                                        </td>
                                        <td style={{ textAlign: "right", fontWeight: "bold" }}>
                                            {formatAmount(Number(totalAmount))}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={4}></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </Box>
    );
};

export default OwnerWiseExpenseReport;
